import React, { useEffect, useState } from "react";

import AdminLayout from "../layouts/AdminLayout";
import PageHeader from "../components/ui/PageHeader";
import Section from "../components/ui/Section";
import StatCard from "../components/ui/StatCard";
import TextInput from "../components/ui/TextInput";
import DocumentsTable from "../components/idc/DocumentsTable";
import DocumentUploadForm from "../components/idc/DocumentUploadForm";

import { analyticsEvents, trackUserEvent } from "../analytics";
import { useUser } from "../contexts/UserContext";

const API =
  process.env.NODE_ENV === "production"
    ? "https://ecovanta.onrender.com"
    : "http://localhost:5000";

export default function AdvisoryDocumentsPage() {
  const { user } = useUser();

  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState("Loading documents...");
  const [uploadStatus, setUploadStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [form, setForm] = useState({
    companyName: "",
    category: "Sustainability Report",
    reportingYear: new Date().getFullYear(),
    tags: "CSRD,ESRS,Climate"
  });

  const [file, setFile] = useState(null);

  async function loadDocuments() {
    try {
      setStatus("Loading documents...");

      const response = await fetch(`${API}/api/advisory/documents`, {
        credentials: "include"
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Unable to load documents.");
      }

      setDocuments(Array.isArray(data) ? data : []);
      setStatus("");
    } catch (error) {
      console.error("Load advisory documents failed:", error);
      setStatus(`Failed to load documents: ${error.message}`);
    }
  }

  useEffect(() => {
    loadDocuments();
  }, []);

  async function handleUpload(event) {
    event.preventDefault();

    if (!file) {
      setUploadStatus("Please select a document first.");
      return;
    }

    try {
      setUploadStatus("Uploading document...");

      const payload = new FormData();
      payload.append("file", file);
      payload.append("companyName", form.companyName);
      payload.append("category", form.category);
      payload.append("reportingYear", form.reportingYear);
      payload.append("tags", form.tags);

      const response = await fetch(`${API}/api/advisory/documents/upload`, {
        method: "POST",
        credentials: "include",
        body: payload
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Upload failed.");
      }

      setUploadStatus("Document uploaded successfully.");

      trackUserEvent(analyticsEvents.DOCUMENT_UPLOADED, user, {
        documentName: data?.document?.originalName || file?.name || "",
        category: data?.document?.category || form.category,
        reportingYear: data?.document?.reportingYear || form.reportingYear,
        storageProvider: data?.document?.storageProvider || "azure"
      });

      setFile(null);
      setForm((prev) => ({
        ...prev,
        companyName: "",
        tags: "CSRD,ESRS,Climate"
      }));

      await loadDocuments();
    } catch (error) {
      console.error("Document upload failed:", error);
      setUploadStatus(`Upload failed: ${error.message}`);
    }
  }

  function handleAnalyzeDocument(doc) {
    trackUserEvent(analyticsEvents.DOCUMENT_ANALYZED, user, {
      documentId: doc._id || doc.id || "",
      documentName: doc.originalName || "",
      category: doc.category || "",
      analysisStatus: doc.analysisStatus || ""
    });

    alert(`AI analysis will be added for: ${doc.originalName || "document"}`);
  }

  const documentStats = {
    total: documents.length,
    companies: new Set(documents.map((doc) => doc.companyName).filter(Boolean))
      .size,
    categories: new Set(documents.map((doc) => doc.category).filter(Boolean))
      .size,
    pending: documents.filter(
      (doc) =>
        !doc.analysisStatus ||
        doc.analysisStatus === "uploaded" ||
        doc.analysisStatus === "queued" ||
        doc.analysisStatus === "pending"
    ).length
  };

  const filteredDocuments = documents.filter((doc) => {
    const searchableText = [
      doc.companyName,
      doc.originalName,
      doc.category,
      doc.reportingYear,
      Array.isArray(doc.tags) ? doc.tags.join(" ") : "",
      doc.analysisStatus
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(searchTerm.toLowerCase());
  });

  return (
    <AdminLayout>
      <main style={{ maxWidth: 1200, margin: "0 auto" }}>
        <PageHeader
          title="Ecovanta Intelligent Document Center"
          subtitle="Upload, organize, and prepare corporate ESG documents for intelligent analysis, ESRS gap detection, and AI-powered advisory workflows."
        />

        <Section
          title="Document Repository Overview"
          subtitle="High-level view of uploaded ESG evidence and advisory documents."
        >
          <div style={statsGridStyle}>
            <StatCard title="Total Documents" value={documentStats.total} />
            <StatCard title="Companies" value={documentStats.companies} />
            <StatCard title="Categories" value={documentStats.categories} />
            <StatCard
              title="Pending Analysis"
              value={documentStats.pending}
              badge="AI"
              tone="warning"
            />
          </div>
        </Section>

        <Section
          title="Upload Advisory Document"
          subtitle="Add sustainability reports, policies, codes of conduct, and other advisory evidence."
        >
          <DocumentUploadForm
            form={form}
            setForm={setForm}
            file={file}
            setFile={setFile}
            uploadStatus={uploadStatus}
            onSubmit={handleUpload}
          />
        </Section>

        <Section title="Uploaded Documents">
          {status && <p>{status}</p>}

          {!status && (
            <>
              <div style={{ marginBottom: 16 }}>
                <TextInput
                  placeholder="Search by company, file name, category, year, tags, or status..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  fullWidth
                />
              </div>

              <DocumentsTable
                documents={filteredDocuments}
                onAnalyzeDocument={handleAnalyzeDocument}
              />
            </>
          )}
        </Section>
      </main>
    </AdminLayout>
  );
}

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
  marginBottom: 24
};